import { useState } from "react";
import { useTranslation } from "react-i18next";

import LanguageSwitcher from "../components/LanguageSwitcher";

import { apiErrorMessage, apiFieldErrors } from "../services/api";
import { verifyTwoFactorChallenge } from "../services/authService";

/**
 * Second step of a 2FA login. The challenge token lives only in the parent's
 * state, so a reload sends the user back to the password form — by design.
 */
export default function TwoFactorChallenge({ challengeToken, email, onVerified, onExpired, onBack }) {
  const { t } = useTranslation();
  const [useRecovery, setUseRecovery] = useState(false);
  const [code, setCode] = useState("");
  const [recoveryCode, setRecoveryCode] = useState("");
  const [errors, setErrors] = useState({});
  const [banner, setBanner] = useState("");
  const [loading, setLoading] = useState(false);

  const switchMode = () => {
    setUseRecovery((on) => !on);
    setErrors({});
    setBanner("");
    setCode("");
    setRecoveryCode("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setBanner("");
    setLoading(true);

    try {
      const { user } = await verifyTwoFactorChallenge(challengeToken, {
        code: useRecovery ? undefined : code,
        recoveryCode: useRecovery ? recoveryCode : undefined,
      });
      onVerified?.(user);
    } catch (err) {
      const status = err.response?.status;

      // An expired challenge or a burned-out attempt counter cannot be retried
      // here — the user needs a fresh password round.
      if (status === 401 || status === 429) {
        onExpired?.(apiErrorMessage(err, t("twoFactor.challenge.expired")));
        return;
      }

      setErrors(apiFieldErrors(err));
      setBanner(apiErrorMessage(err, t("twoFactor.challenge.failed")));
    } finally {
      setLoading(false);
    }
  };

  const fieldError = useRecovery ? errors.recovery_code?.[0] : errors.code?.[0];
  const submittable = useRecovery ? recoveryCode.trim().length > 0 : code.length === 6;

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <form onSubmit={handleSubmit} className="card w-full max-w-sm p-6" noValidate>
        <div className="mb-4 flex justify-end">
          <LanguageSwitcher compact />
        </div>

        <h1 className="mb-1 text-center text-xl font-bold">{t("twoFactor.challenge.title")}</h1>
        <p className="mb-6 text-center text-sm text-ink-2">
          {useRecovery ? t("twoFactor.challenge.recoveryHint") : t("twoFactor.challenge.hint")}
          {email && <span className="mt-1 block font-medium text-ink">{email}</span>}
        </p>

        {banner && !fieldError && (
          <div className="mb-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
            {banner}
          </div>
        )}

        {useRecovery ? (
          <>
            <label className="field-label" htmlFor="recovery-code">
              {t("twoFactor.challenge.recoveryLabel")}
            </label>
            <input
              id="recovery-code"
              type="text"
              className="field-input"
              value={recoveryCode}
              onChange={(e) => setRecoveryCode(e.target.value)}
              autoComplete="one-time-code"
              autoFocus
              disabled={loading}
            />
          </>
        ) : (
          <>
            <label className="field-label" htmlFor="totp-code">
              {t("twoFactor.challenge.codeLabel")}
            </label>
            <input
              id="totp-code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              className="field-input text-center text-lg font-semibold tracking-[0.5em]"
              value={code}
              // Authenticator codes are digits only; strip anything pasted in.
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              autoComplete="one-time-code"
              autoFocus
              disabled={loading}
            />
          </>
        )}

        {fieldError && <p className="field-error">{fieldError}</p>}

        <button type="submit" disabled={loading || !submittable} className="btn-primary mt-6 w-full">
          {loading ? t("twoFactor.challenge.verifying") : t("twoFactor.challenge.verify")}
        </button>

        <div className="mt-4 flex flex-col items-center gap-2 text-sm">
          <button
            type="button"
            onClick={switchMode}
            disabled={loading}
            className="font-medium text-accent-text hover:text-accent-text"
          >
            {useRecovery ? t("twoFactor.challenge.useCode") : t("twoFactor.challenge.useRecovery")}
          </button>
          <button
            type="button"
            onClick={onBack}
            disabled={loading}
            className="text-ink-2 hover:text-ink"
          >
            {t("twoFactor.challenge.backToLogin")}
          </button>
        </div>
      </form>
    </div>
  );
}
